import { Settings } from '../../Settings';
import Vector from "../math/Vector";
import GridBox from "./GridBox";
import Grid from './Grid';
import GridTile from "./GridTile";
import GridRegion from './GridRegion';
import WorldGrid from './WorldGrid';
import Fast from '../../util/Fast';

export default class GridZone extends GridBox {
    /**
     * @param {Grid} grid 
     * @param {Vector} origin 
     */
    constructor(grid, origin) {
        super(origin);

        /**
         * @constant
         * @type {Grid}
         */
        this.grid = grid;

        /**
         * @constant
         * @type {Array.<GridRegion}
         */
        this.regions = this.createRegions();
    }

    /**
     * @private
     * @returns {Array.<GridRegion>}
     */
    createRegions() {

        const origin = this.getOrigin();
        const x = origin.x;
        const y = origin.y;

        const regions = new Array(Settings.grid.numRegionColsInZone * Settings.grid.numRegionRowsInZone);

        let i = 0;
        for (let col = 0; col < Settings.grid.numRegionColsInZone; col++) {
            for (let row = 0; row < Settings.grid.numRegionRowsInZone; row++) {
                regions[i++] = new GridRegion(this, new Vector(x + (Settings.grid.regionPixelWidth * col), y + (Settings.grid.regionPixelHeight * row)));
            }
        }

        return regions;
    }

    /**
     * Invoked when the parent grid is assigned a new location.
     * @param {number} originX 
     * @param {number} originY 
     */
    initialize(originX, originY) {

        // Update the origin to be relative to the grid origin.
        // const origin = this.getOrigin();
        this.origin.set(originX, originY);
        const x = this.origin.x;
        const y = this.origin.y;

        // Initialize the regions in the zone.
        let i = 0;
        for (let col = 0; col < Settings.grid.numRegionColsInZone; col++) {
            for (let row = 0; row < Settings.grid.numRegionRowsInZone; row++) {
                const region = this.regions[i++];
                region.initialize(x + (Settings.grid.regionPixelWidth * col), y + (Settings.grid.regionPixelHeight * row));
            }
        }
    }

    /**
     * @returns {WorldGrid}
     */
    getWorldGrid() {
        return this.grid.getWorldGrid();
    }

    /**
     * @returns {Grid}
     */
    getGrid() {
        return this.grid;
    }

    /**
     * @returns {Array.<GridRegion>}
     */
    getRegions() {
        return this.regions;
    }

    /**
     * @returns {number}
     */
    getPixelWidth() {
        return Settings.grid.zonePixelWidth;
    }

    /**
     * @returns {number}
     */
    getPixelHeight() {
        return Settings.grid.zonePixelHeight;
    }

    /**
     * @param {number} tileCol 
     * @param {number} tileRow 
     * @returns {GridTile}
     */
    getGridTileAbsolute(tileCol, tileRow) {
        const origin = this.origin;
        const tileOriginCol = Fast.floor(origin.x / Settings.tile.size);
        const tileOriginRow = Fast.floor(origin.y / Settings.tile.size);

        return this.getGridTileLocal(tileCol - tileOriginCol, tileRow - tileOriginRow);
    }

    /**
     * @param {number} tileCol 
     * @param {number} tileRow 
     * @returns {GridTile}
     */
    getGridTileLocal(tileCol, tileRow) {
        // // If the grid is null, then the zone is not a part of the world yet.
        // if (!this.grid || !this.regions) {
        //     return null;
        // }

        const regionCol = Fast.floor(tileCol / Settings.grid.regionTileWidth);
        const regionRow = Fast.floor(tileRow / Settings.grid.regionTileHeight);

        if (regionCol < 0 || regionCol >= Settings.grid.numRegionColsInZone) {
            return null;
        }
        if (regionRow < 0 || regionRow >= Settings.grid.numRegionRowsInZone) {
            return null;
        }

        const index = (regionCol * Settings.grid.numRegionRowsInZone) + regionRow;
        if (index >= 0 && index < this.regions.length) {
            const region = this.regions[index];

            const zoneOrigin = this.getOrigin();
            const zoneTileOriginCol = Fast.floor(zoneOrigin.x / Settings.tile.size);
            const zoneTileOriginRow = Fast.floor(zoneOrigin.y / Settings.tile.size);

            const regionOrigin = region.getOrigin();
            const regionTileOriginCol = Fast.floor(regionOrigin.x / Settings.tile.size);
            const regionTileOriginRow = Fast.floor(regionOrigin.y / Settings.tile.size);

            return region.getGridTileLocal(tileCol + zoneTileOriginCol - regionTileOriginCol, tileRow + zoneTileOriginRow - regionTileOriginRow);
        }
        else {
            console.log("GridZone.getGridTileLocal() Failed to find tile. col=" + tileCol + " row=" + tileRow + " index=" + index);
            return null;
        }
    }

    /**
     * @private
     */
    findTileNeighborsFull() {
        // This is invoked after all the regions have been added to the zone.
        for (let i = 0; i < this.regions.length; i++) {
            // Find neighbors for all the tiles in the region.
            this.regions[i].findTileNeighborsFull();
        }
    }

    findRegionNeighborsBorderEast() {
        // East edge: col = numCols - 1
        const eastCol = Settings.grid.numRegionColsInZone - 1;
        for (let row = 0; row < Settings.grid.numRegionRowsInZone; row++) {
            this.findRegion(eastCol, row).findTileNeighborsBorderRight();
        }
    }

    findRegionNeighborsBorderWest() {
        // West edge: col = 0
        for (let row = 0; row < Settings.grid.numRegionRowsInZone; row++) {
            this.findRegion(0, row).findTileNeighborsBorderLeft();
        }
    }

    findRegionNeighborsBorderSouth() {
        // South edge: row - numRows - 1
        const southRow = Settings.grid.numRegionColsInZone - 1;
        for (let col = 0; col < Settings.grid.numRegionColsInZone; col++) {
            this.findRegion(col, southRow).findTileNeighborsBorderBottom();
        }
    }

    findRegionNeighborsBorderNorth() {
        // North edge: row = 0
        for (let col = 0; col < Settings.grid.numRegionColsInZone; col++) {
            this.findRegion(col, 0).findTileNeighborsBorderTop();
        }
    }


    /**
     * @param {number} col
     * @param {number} row
     * @returns {GridRegion}
     */
    findRegion(col, row) {
        return this.regions[(col * Settings.grid.numRegionRowsInZone) + row];
    }

    // tile neighbors for zone/zone boundaries
    findZoneBorderRegionNeighbors() {
        // The origin is at the bottom left corner
        // South row is 0, North is num rows - 1
        const eastCol = Settings.grid.numRegionColsInZone - 1;
        const northRow = Settings.grid.numRegionRowsInZone - 1;
        const southRow = 0;
        const westCol = 0;

        // Corner Regions
        const regionNE = this.findRegion(eastCol, northRow);
        const regionSE = this.findRegion(eastCol, southRow);
        const regionNW = this.findRegion(westCol, northRow);
        const regionSW = this.findRegion(westCol, southRow);

        // North East
        regionNE.findTileNeighborsBorderTop();
        regionNE.findTileNeighborsBorderRight();

        // South East
        regionSE.findTileNeighborsBorderBottom();
        regionSE.findTileNeighborsBorderRight();

        // North West
        regionNW.findTileNeighborsBorderTop();
        regionNW.findTileNeighborsBorderLeft();

        // South West
        regionSW.findTileNeighborsBorderBottom();
        regionSW.findTileNeighborsBorderLeft();

        // Zones are always 2x2 in a grid, so they are all corners.
        // Really no need to process regions along the size of the a zone, because its all corners.
        if (Settings.grid.numRegionRowsInZone > 2) {
            // East, minus the corners
            for (let row = 1; row < Settings.grid.numRegionRowsInZone - 1; row++) {
                this.findRegion(eastCol, row).findTileNeighborsBorderRight();
            }

            // West, excluding corners
            for (let row = 1; row < Settings.grid.numRegionRowsInZone - 1; row++) {
                this.findRegion(westCol, row).findTileNeighborsBorderLeft();
            }
        }

        if (Settings.grid.numRegionColsInZone > 2) {
            // South, excluding corners
            for (let col = 1; col < Settings.grid.numRegionColsInZone - 1; col++) {
                this.findRegion(col, southRow).findTileNeighborsBorderBottom();
            }

            // North, excluding corners
            for (let col = 1; col < Settings.grid.numRegionColsInZone - 1; col++) {
                this.findRegion(col, northRow).findTileNeighborsBorderTop();
            }
        }
    }

    findTileNeighborsZoneBorderRight() {
        // East edge: col = numCols - 1
        const eastCol = Settings.grid.numRegionColsInZone - 1;
        for (let row = 0; row < Settings.grid.numRegionRowsInZone; row++) {
            this.findRegion(eastCol, row).findTileNeighborsBorderRight();
        }
    }
    nullifyTileNeighborsZoneBorderRight() {
        // East edge: col = numCols - 1
        const eastCol = Settings.grid.numRegionColsInZone - 1;
        for (let row = 0; row < Settings.grid.numRegionRowsInZone; row++) {
            this.findRegion(eastCol, row).nullifyTileNeighborsBorderRight();
        }
    }

    findTileNeighborsZoneBorderLeft() {
        // West edge: col = 0
        for (let row = 0; row < Settings.grid.numRegionRowsInZone; row++) {
            this.findRegion(0, row).findTileNeighborsBorderLeft();
        }
    }
    nullifyTileNeighborsZoneBorderLeft() {
        // West edge: col = 0
        for (let row = 0; row < Settings.grid.numRegionRowsInZone; row++) {
            this.findRegion(0, row).nullifyTileNeighborsBorderLeft();
        }
    }

    findTileNeighborsZoneBorderBottom() {
        const southRow = Settings.grid.numRegionRowsInZone - 1;
        for (let col = 0; col < Settings.grid.numRegionColsInZone; col++) {
            this.findRegion(col, southRow).findTileNeighborsBorderBottom();
        }
    }
    nullifyTileNeighborsZoneBorderBottom() {
        const southRow = Settings.grid.numRegionRowsInZone - 1;
        for (let col = 0; col < Settings.grid.numRegionColsInZone; col++) {
            this.findRegion(col, southRow).nullifyTileNeighborsBorderBottom();
        }
    }

    findTileNeighborsZoneBorderTop() {
        for (let col = 0; col < Settings.grid.numRegionColsInZone; col++) {
            this.findRegion(col, 0).findTileNeighborsBorderTop();
        }
    }
    nullifyTileNeighborsZoneBorderTop() {
        for (let col = 0; col < Settings.grid.numRegionColsInZone; col++) {
            this.findRegion(col, 0).nullifyTileNeighborsBorderTop();
        }
    }

    ///////////////////////////////////////
    // Tile Border Evaluation
    ///////////////////////////////////////

    evaluateTileBordersRight() {
        // East edge: col = numCols - 1
        const eastCol = Settings.grid.numRegionColsInZone - 1;
        for (let row = 0; row < Settings.grid.numRegionRowsInZone; row++) {
            this.findRegion(eastCol, row).evaluateTileBordersRight();
        }
    }

    evaluateTileBordersLeft() {
        // West edge: col = 0
        for (let row = 0; row < Settings.grid.numRegionRowsInZone; row++) {
            this.findRegion(0, row).evaluateTileBordersLeft();
        }
    }

    evaluateTileBordersBottom() {
        const southRow = Settings.grid.numRegionRowsInZone - 1;
        for (let col = 0; col < Settings.grid.numRegionColsInZone; col++) {
            this.findRegion(col, southRow).evaluateTileBordersBottom();
        }
    }

    evaluateTileBordersTop() {
        for (let col = 0; col < Settings.grid.numRegionColsInZone; col++) {
            this.findRegion(col, 0).evaluateTileBordersTop();
        }
    }
}